import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Lightning, Trash, PencilSimple, FloppyDisk, X, CircleNotch } from "@phosphor-icons/react";

const pick = (t) => ({ title: t.title || "", category: t.category || "general", content: t.content || "", content_en: t.content_en || "" });

export default function TemplateRow({ t, lang, categories, onSave, onToggle, onDelete }) {
  const T = (es, en) => (lang === "es" ? es : en);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(pick(t));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    const ok = await onSave(t.id, form);
    setSaving(false);
    if (ok) setEditing(false);
  };
  const cancel = () => { setForm(pick(t)); setEditing(false); };

  return (
    <div className={`rounded-md border p-2.5 ${t.source === "bdc_seed" ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-background"} ${t.enabled ? "" : "opacity-50"} ${editing ? "ring-2 ring-primary/40" : ""}`}
      data-testid={`template-row-${t.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          <Lightning size={11} weight="fill" className="text-primary shrink-0" />
          {editing ? (
            <>
              <Input value={form.title} onChange={set("title")} className="h-7 text-xs font-medium flex-1 min-w-[180px]" data-testid={`template-edit-title-${t.id}`} />
              <select value={form.category} onChange={set("category")} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid={`template-edit-category-${t.id}`}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          ) : (
            <span className="text-xs font-medium" data-testid={`template-title-${t.id}`}>{t.title}</span>
          )}
          {t.source === "bdc_seed" && <Badge className="text-[9px] uppercase bg-amber-500/90 text-white border-transparent shrink-0">BDC</Badge>}
          {t.code && <code className="text-[9px] text-muted-foreground bg-muted px-1 rounded shrink-0">{t.code}</code>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <Button size="sm" className="h-7 px-2 text-xs gap-1" onClick={save} disabled={saving || !form.title.trim() || !form.content.trim()} data-testid={`template-save-${t.id}`}>
                {saving ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />} {T("Guardar", "Save")}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" onClick={cancel} data-testid={`template-cancel-${t.id}`}>
                <X size={12} /> {T("Cancelar", "Cancel")}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" onClick={() => setEditing(true)} data-testid={`template-edit-${t.id}`}>
                <PencilSimple size={12} /> {T("Editar", "Edit")}
              </Button>
              <Switch checked={t.enabled} onCheckedChange={(v) => onToggle(t.id, v)} data-testid={`template-enabled-${t.id}`} />
              <button onClick={() => onDelete(t.id)} className="text-destructive hover:bg-destructive/10 p-1 rounded" data-testid={`template-delete-${t.id}`}><Trash size={11} /></button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {[["content", "ES"], ["content_en", "EN"]].map(([k, label]) => (
          <div key={k}>
            <div className="text-[9px] uppercase text-muted-foreground mb-1">{label}</div>
            {editing ? (
              <Textarea value={form[k]} onChange={set(k)} rows={4} className="text-[11px]" data-testid={`template-edit-${k}-${t.id}`}
                placeholder={k === "content_en" ? T("Traducción EN (opcional)", "EN translation (optional)") : ""} />
            ) : (
              <div className="text-[11px] whitespace-pre-wrap rounded-md bg-muted/40 px-2 py-1.5 min-h-[3rem]" data-testid={`template-${k}-${t.id}`}>
                {t[k] || <span className="text-muted-foreground italic">{T("Sin traducción", "No translation")}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
